import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { EntityCollectionService, EntityServices } from '@ngrx/data';
import { FormGroupState } from 'ngrx-forms';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, Subject } from 'rxjs/index';
import { filter, map, takeUntil } from 'rxjs/operators';
import { BsModalConfirmComponent } from '../../../../../core/modal/modal-confirm.component';
import { User } from '../../../../../core/model/user.model';
import { GatewayActions } from '../../../../../core/state/gateway.actions';
import { MediaDevice } from '../../models/user-device.model';
import { availableDeviceDataServiceConfig } from '../available-devices.entity';
import { AvailableDevicesSelectors } from '../state/available-devices.selector';
import { UserDevicesActions } from '../state/user-devices.actions';
import { UserDevicesSelectors } from '../state/user-devices.selectors';
import { AdminUserDevicesFormValue } from '../state/user-devices.state';
import { UserSelectors } from '../../../../../user/state/user.selectors';

@Component({
    selector: 'admin-user-devices',
    templateUrl: './devices.component.html',
    styleUrls: ['./devices.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminUserDevicesComponent implements OnInit, AfterViewInit, OnDestroy {

    private ngUnsubscribe = new Subject();

    public loadIndicator$: Observable<boolean>;
    public selectedUser$: Observable<User>;
    // public selectedUserId$: Observable<number>;
    public selectedUserPermissions$: Observable<Array<any>>;
    public hasTwoWayPermission$: Observable<boolean>;
    public hasTwoWayPermission = false;

    private selectedUserId = 0; // Used to track if different user is selected

    formState$: Observable<FormGroupState<AdminUserDevicesFormValue>>;

    // availableDevices$: Observable<Array<MediaDevice>>;
    devicesSms$: Observable<Array<MediaDevice>>;
    devicesEmail$: Observable<Array<MediaDevice>>;
    devicesVoice$: Observable<Array<MediaDevice>>;

    private availableDevicesService: EntityCollectionService<MediaDevice>;

    constructor(private modalService: BsModalService,
                private userDevicesActions: UserDevicesActions,
                private gatewayActions: GatewayActions,
                private selectors: UserDevicesSelectors,
                private availableDevicesSelectors: AvailableDevicesSelectors,
                private entityServices: EntityServices,
                private userSelectors: UserSelectors
    ) {
        // Setup services, selectors and bindings

        this.availableDevicesService =
            this.entityServices.getEntityCollectionService<MediaDevice>(availableDeviceDataServiceConfig.entityName);

        // Setup state selectors
        this.loadIndicator$ = selectors.selectLoadingStatus();
        this.formState$ = selectors.selectFormState();
        this.devicesSms$ = availableDevicesSelectors.selectSMSDevices();
        this.devicesEmail$ = availableDevicesSelectors.selectEmailDevices();
        this.devicesVoice$ = availableDevicesSelectors.selectVoiceDevices();
        this.selectedUser$ = this.userSelectors.selectSelectedUser();
        this.selectedUserPermissions$ = this.userSelectors.selectSelectedUserPermissions();
   }

    ngOnInit(): void {
        this.devicesVoice$ = this.devicesVoice$
            .pipe(
                map((deviceVoices) => {
                const index = deviceVoices.findIndex(devices => devices.deviceId.indexOf('pnova') !== -1)
                deviceVoices.splice(index, 1);

                return deviceVoices;
                })
            );
    }

    ngAfterViewInit(): void {
        this.selectedUser$
            .pipe(
                filter(user => user !== undefined),
                filter(user => user.userId !== 0),
                filter(user => user.userId !== this.selectedUserId),
                takeUntil(this.ngUnsubscribe)
            )
            .subscribe(user => {
                this.selectedUserId = user.userId;
                this.userDevicesActions.getUserDevices(undefined);
                this.availableDevicesService.getAll(); // trigger an action to get available devices
                this.gatewayActions.getSharedNumbers(undefined);
            });

        this.hasTwoWayPermission$ = this.selectedUserPermissions$
            .pipe(
                filter(permissions => permissions !== undefined),
                filter(permissions => permissions.length !== 0),
                map(permissions => permissions.findIndex(permission => permission.permissionKey === 'twoway') >= 0)
            );
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    // TODO: Do a lookup call to the backend after entering a phone number (after a pause/blur) to reduce risk of failing saving

    saveDevices(): void {
        this.userDevicesActions.saveUserDevices(undefined);
    }

    showSharedNumbersDialog(): void {
        this.userDevicesActions.openSharedNumbersDialog(undefined);
    }

    unMarkAsShared(smsDevice): any {
        // NOTE: keypress event is not fired on delete button and change event not until blur
        // TODO: Find a better way (e.g. in the reducer or side effect to do this on changes to the userSenderAddress
        if (smsDevice.controls.isSharedNumber !== undefined && smsDevice.controls.isSharedNumber.value) {
            // TODO: On changes - check the list of shared numbers
            this.userDevicesActions.unMarkAsSharedNumber(undefined);
        }
    }

    // TODO: Refactor availableDevices to include if devices can take phone number
    isTwoWayAbleDevice(deviceId): any {
        const twoWayAbleDevices = ['twilio', 'infobip', 'messagemedia'];

        return twoWayAbleDevices.find(device => deviceId.indexOf(device) > -1);
    }

    private saveOrDeleteChanges(title: string, message: string): any {
        const modalRef = this.modalService.show(BsModalConfirmComponent,
            {
                initialState: {
                    title,
                    message
                },
                backdrop: 'static',
                keyboard: false // disable esc key
            });
        modalRef.content.onClose
            .subscribe(confirmedDelete => {
                if (confirmedDelete) {
                    this.saveDevices();
                } else {
                    console.log(' cancel');
                }
            });
    }

}
